import React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { DeckStore, CardStore, UserStore } from '@seedlang/stores';
import Copy from 'components/copy';
import autobind from 'autobind-decorator';
import Spinner from 'components/spinner';
import { computed, observable } from 'mobx';
import Paginator from 'components/paginator';
import SortableColumns from 'components/hoc/sortable_columns';
import cx from 'classnames';
import { Link } from 'react-router';
import Filter from 'components/backend_filter/filter';
import { Constants } from '@seedlang/constants';
import Modal from 'components/modal';
import ReviewDetails from 'components/card/review_details';
import SearchInput from 'components/form/search_input';
import { isPresent } from '@seedlang/utils';
import DeleteButton from 'components/button/delete_button';
import InPlaceSelect from 'components/form/in_place_select';
import InPlaceTextInput from 'components/form/in_place_text_input';

const Wrapper = styled.div`
  .table-container {
    overflow-x: auto;
    width: 100%;
  }

  .table-wrapper {
    min-width: 1200px;
    
    th {
      white-space: nowrap;
    }
  }
`;

@observer
class ReviewsIndex extends React.Component {
  @observable isLoading = true;
  @observable selectedCard = null;
  @observable showDetailsModal = false;
  @observable searchQuery = '';

  constructor(props) {
    super(props);
    this.loadUser();
    this.loadDeck();
  }

  @computed get queryStrings() {
    const query = {
      deck_id: this.props.params.deckId,
      user_id: this.props.params.userId,
      include_count: true,
    };
    if (isPresent(this.searchQuery)) {
      query.search_query = this.searchQuery;
    }
    return query;
  }

  @autobind loadUser() {
    if (!UserStore.showData || UserStore.showData.id !== this.props.params.userId) {
      UserStore.getShow({ids: {userId: this.props.params.userId}});
    }
  }

  @autobind loadDeck() {
    if (!DeckStore.indexData || !DeckStore.indexData.find(d => d.id == this.props.params.deckId)) {
      DeckStore.getIndex({queryStrings: {user_id: this.props.params.userId}, limit: 5});
    }
  }

  @computed get cards() {
    return CardStore.indexData || [];
  }

  @computed get deckName() {
    const deck = DeckStore.indexData.find(d => d.id == this.props.params.deckId);
    return deck ? `${deck.name} (${deck.languageId})` : 'Loading...';
  }

  getCardText(card, side) {
    if (side === 'front') {
      if (card.frontText) return card.frontText;
      if (card.word?.targetTextWithDefiniteArticle) return card.word.targetTextWithDefiniteArticle;
      if (card.sentence?.targetText) return card.sentence.targetText;
      return 'No front text';
    } else {
      if (card.backText) return card.backText;
      if (card.word?.vocabSourceList) return card.word.vocabSourceList;
      if (card.sentence?.sourceText) return card.sentence.sourceText;
      return '';
    }
  }

  truncateText(text, maxLength = 25) {
    if (!text) return '-';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  }

  @autobind openDetailsModal(card) {
    this.selectedCard = card;
    this.showDetailsModal = true;
  }

  @autobind closeDetailsModal() {
    this.showDetailsModal = false;
    this.selectedCard = null;
  }

  @autobind onSortColumn(column) {
    this.props.onSortColumn(column, false, { queryStrings: this.queryStrings });
  }

  @autobind onSearchChange(text) {
    this.searchQuery = text || '';
    CardStore.getIndex({ queryStrings: this.queryStrings });
  }

  @autobind onDelete(cardId) {
    CardStore.destroy({ ids: { cardId } }, () => {
      CardStore.getIndex({ queryStrings: this.queryStrings });
    });
  }

  @autobind onCardTypeChange(cardId, newCardTypeId) {
    CardStore.update({ ids: { cardId }, data: { card_type_id: newCardTypeId } }, () => {
      // Update the local card data to reflect the change
      const card = this.cards.find(c => c.id === cardId);
      if (card) {
        card.cardTypeId = newCardTypeId;
      }
    });
  }

  @autobind onFrontTextChange(cardId, newText) {
    CardStore.update({ ids: { cardId }, data: { front_text: newText } }, () => {
      const card = this.cards.find(c => c.id === cardId);
      if (card) {
        card.frontText = newText;
      }
    });
  }

  @autobind onBackTextChange(cardId, newText) {
    CardStore.update({ ids: { cardId }, data: { back_text: newText } }, () => {
      const card = this.cards.find(c => c.id === cardId);
      if (card) {
        card.backText = newText;
      }
    });
  }

  isCustomCard(card) {
    // A card is custom if it has frontText/backText and no word/sentence
    return card.frontText && !card.word && !card.sentence;
  }

  render() {
    const loading = CardStore.requestCounter > 0 || DeckStore.requestCounter > 0;

    return (
      <Wrapper>
        <div className='reviews-index'>
          <div className='breadcrumbs-wrapper'>
            <div className='breadcrumbs'>
              <Link to={{ name: 'builder.users.index' }}>
                Users
              </Link>
              <i className='fa fa-angle-double-right' />
              <Link to={{ name: 'builder.users.edit', params: { userId: this.props.params.userId } }}>
                {UserStore.showData.name || '...'}
              </Link>
              <i className='fa fa-angle-double-right' />
              <span>Review Cards - {this.deckName}</span>
            </div>
          </div>

          <div style={{ marginBottom: '10px' }}>
            <SearchInput
              onChange={this.onSearchChange}
              value={this.searchQuery}
              placeholder='Search cards by text...'
              hideSpecialCharacters
            />
          </div>

          <Filter
            store={CardStore}
            defaultSort='-created_at'
            namespace='reviews-index'
            queryStrings={this.queryStrings}
            filters={[
              {
                type: 'select',
                name: 'card_type_id',
                label: 'Card Type',
                options: Constants.REVIEW_CARD_TYPES.map(type => ({ id: type.id, name: type.name })),
                labelField: 'name',
                default: '',
              },
              {
                type: 'date',
                name: 'created_at',
                label: 'Created Date',
                default: '',
              },
              {
                type: 'date',
                name: 'review_after',
                label: 'Next Due Date',
                default: '',
              },
              {
                type: 'boolean',
                name: 'retired',
                label: 'Retired',
              },
            ]}
          />

          {loading && <Spinner />}

          {
            !loading && this.cards.length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                No review cards found for this deck and user.
              </div>
            )
          }

          {
            !loading && this.cards.length > 0 && (
              <>
                <div className='table-container'>
                  <table className='table-wrapper'>
                  <thead>
                    <tr>
                      <th>Card ID</th>
                      <th>Front Text</th>
                      <th>Back Text</th>
                      <th>Type</th>
                      <th
                        className={cx('sortable', this.props.columnClassNames('difficulty_score'))}
                        onClick={() => this.onSortColumn('difficulty_score')}
                        width='60'
                      >
                        Difficulty
                      </th>
                      <th
                        className={cx('sortable', this.props.columnClassNames('review_after'))}
                        onClick={() => this.onSortColumn('review_after')}
                      >
                        Next Due
                      </th>
                      <th
                        className={cx('sortable', this.props.columnClassNames('created_at'))}
                        onClick={() => this.onSortColumn('created_at')}
                      >
                        Created
                      </th>
                      <th width='60'>Status</th>
                      <th width='100'>Reviews</th>
                      <th>Note</th>
                      <th/>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      this.cards.map(card => {
                        return (
                          <tr key={card.id}>
                            <td>
                              <Copy text={card.id} />
                            </td>
                            <td>
                              {this.isCustomCard(card) ? (
                                <InPlaceTextInput
                                  value={card.frontText || ''}
                                  onChange={newText => this.onFrontTextChange(card.id, newText)}
                                  submitOnEnter={true}
                                  onSubmit={newText => this.onFrontTextChange(card.id, newText)}
                                  style={{ width: '100%', padding: '4px' }}
                                />
                              ) : (
                                <div
                                  className='text'
                                  title={this.getCardText(card, 'front')}
                                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                                >
                                  {
                                    isPresent(card.word) && (
                                      <Link target='_blank' to={{ name: 'builder.words.edit', params: { wordId: card.word.id } }}>
                                        <i className='fa fa-chevron-right fa-block' />
                                      </Link>
                                    )
                                  }
                                  {
                                    isPresent(card.sentence) && (
                                      <Link target='_blank' to={{ name: 'builder.sentences.edit', params: { sentenceId: card.sentence.id } }}>
                                        <i className='fa fa-chevron-right fa-block' />
                                      </Link>
                                    )
                                  }
                                  {this.truncateText(this.getCardText(card, 'front'))}
                                </div>
                              )}
                            </td>
                            <td>
                              {this.isCustomCard(card) ? (
                                <InPlaceTextInput
                                  value={card.backText || ''}
                                  onChange={newText => this.onBackTextChange(card.id, newText)}
                                  submitOnEnter={true}
                                  onSubmit={newText => this.onBackTextChange(card.id, newText)}
                                  style={{ width: '100%', padding: '4px' }}
                                />
                              ) : (
                                <div className='text' title={this.getCardText(card, 'back')}>
                                  {this.truncateText(this.getCardText(card, 'back'))}
                                </div>
                              )}
                            </td>
                            <td>
                              <InPlaceSelect
                                value={card.cardTypeId}
                                options={Constants.REVIEW_CARD_TYPES.map(type => [type.id, type.name])}
                                onChange={newValue => this.onCardTypeChange(card.id, newValue)}
                                clickToEdit={true}
                                allowUpdate={true}
                                width='150px'
                              />
                            </td>
                            <td>
                              <div className='text'>
                                {card.difficultyScore || 0}
                              </div>
                            </td>
                            <td>
                              <div className='text'>
                                {card.reviewAfter?.formattedDateWithYear || 'Never'}
                              </div>
                            </td>
                            <td>
                              <div className='text'>
                                {card.createdAt?.formattedDateWithYear || 'Never'}
                              </div>
                            </td>
                            <td>
                              <div className='text'>
                                {card.retired ? 'Retired' : 'Active'}
                              </div>
                            </td>
                            <td>
                              <button
                                className='btn btn-sm btn-primary'
                                style={{cursor: 'pointer'}}
                                onClick={() => this.openDetailsModal(card)}
                              >
                                {`${card.reviewsCount || 0} Reviews`}
                              </button>
                            </td>
                            <td>
                              <div className='text' title={card.note || ''}>
                                {this.truncateText(card.note)}
                              </div>
                            </td>
                            <td>
                              <DeleteButton
                                message='Delete this card?'
                                onConfirm={() => this.onDelete(card.id)}
                                allowDeletion
                                faIcon='fa-trash'
                                iconColor='#dc3545'
                              />
                            </td>
                          </tr>
                        );
                      })
                    }
                  </tbody>
                </table>
                </div>

                <Paginator
                  store={CardStore}
                  queryStrings={this.queryStrings}
                  limit={20}
                />
              </>
            )
          }

          {
            this.showDetailsModal && this.selectedCard && (
              <Modal
                onCloseModal={this.closeDetailsModal}
                animate
                width='800px'
                title='Review Details'
                gradient
                height='800px'
                contentShouldScroll
              >
                <ReviewDetails card={this.selectedCard} />
              </Modal>
            )
          }
        </div>
      </Wrapper>
    );
  }
}

export default SortableColumns(ReviewsIndex, CardStore, { sortColumn: 'created_at', sortIsAscending: false });